/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback } from 'react';
import { Row, Select } from 'antd';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { getIdMap } from 'ontraccr-common/lib/Common';

import CompanySettingsCard from '../CompanySettingsCard';
import { getBucketTemplateTableColumns } from '../../forms/formHelpers';
import HoverHelp from '../../common/HoverHelp';
import { workReportEntryDisplayFieldOptions } from '../../boards/BoardCardABWorkReporting/BoardCardABWorkReportingHelpers';
import BoardStatusDisplayFieldCard from '../../boards/BoardStatusDisplayFieldCard';

const convertTableColumnsToOptions = (tableColumns) => (
  Object.keys(tableColumns).map((key) => ({
    label: tableColumns[key],
    value: key,
  }))
);

const HoverHelpComponent = () => (
  <HoverHelp
    content="If no fields are selected, then all fields will be displayed."
    placement="top"
  />
);

const WorkReportEntryHoverHelpComponent = () => (
  <HoverHelp
    content="Added fields will be displayed on the work reports page."
    placement="top"
  />
);

export default function ABWorkReportingDisplayFields({
  bucketTemplateMap,
  abWorkReportingMapping,
  saveDisplayFields,
  userDisplayFields = {},
  isWorkReportEntryMapping = false,
}) {
  const {
    itemId,
    segmentId,
    bidItemId,
  } = abWorkReportingMapping;

  const {
    itemDisplayFields = [],
    segmentDisplayFields = [],
    bidItemDisplayFields = [],
    workReportEntryDisplayFields = [],
  } = userDisplayFields;

  const itemDisplayFieldOptions = convertTableColumnsToOptions(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[itemId],
      additionalBaseOptions: {
        numberOfSegments: 'Segment Count',
        numberOfBidItems: 'Bid Item Count',
      },
    }),
  );
  const segmentDisplayFieldOptions = convertTableColumnsToOptions(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[segmentId],
      additionalBaseOptions: {
        numberOfBidItems: 'Bid Item Count',
      },
    }),
  );
  const bidItemDisplayFieldOptions = convertTableColumnsToOptions(
    getBucketTemplateTableColumns({
      data: bucketTemplateMap[bidItemId],
      additionalBaseOptions: {
        repQty: 'Bid Item Rep Qty',
        number: 'Task Number',
      },
    }),
  );

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const ABWorkReportingDisplayFieldsCard = useCallback(({
    displayFieldOptions,
    displayFields,
    title,
    propertyName,
  }) => {
    const optionsMap = getIdMap(displayFieldOptions, 'value');

    const onFieldsChange = (newFields) => {
      saveDisplayFields({ [propertyName]: newFields });
    };

    const onDragEnd = (result) => {
      if (!result.destination) {
        return;
      }
      const items = reorder(
        displayFields,
        result.source.index,
        result.destination.index,
      );
      onFieldsChange(items);
    };

    const onDeleteClicked = (id) => {
      const newFields = displayFields.filter((val) => val !== id);
      onFieldsChange(newFields);
    };

    return (
      <>
        <Row style={{ marginTop: 10, marginBottom: 5 }}>{title} Display Fields: </Row>
        <Select
          options={displayFieldOptions}
          value={displayFields}
          onChange={onFieldsChange}
          style={{ width: '100%' }}
          mode="multiple"
        />
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`droppable-${propertyName}`}>
            {({ innerRef, droppableProps, placeholder }) => (
              <div
                ref={innerRef}
                {...droppableProps}
                style={{ marginBottom: 10 }}
              >
                {displayFields.map((val, index) => (
                  <BoardStatusDisplayFieldCard
                    key={val}
                    id={val}
                    index={index}
                    label={optionsMap?.[val]?.label ?? val}
                    onDeleteClicked={() => onDeleteClicked(val)}
                  />
                ))}
                {placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </>
    );
  }, [userDisplayFields, saveDisplayFields]);

  if (!itemId && !segmentId && !bidItemId) {
    return null;
  }

  if (isWorkReportEntryMapping) {
    // Remove the additional base options from the display field options
    const itemOptions = itemDisplayFieldOptions.filter((opt) => opt.value !== 'numberOfSegments' && opt.value !== 'numberOfBidItems');
    const segmentOptions = segmentDisplayFieldOptions.filter((opt) => opt.value !== 'numberOfBidItems');
    const bidItemOptions = bidItemDisplayFieldOptions.filter((opt) => opt.value !== 'repQty');
    const options = [
      ...workReportEntryDisplayFieldOptions,
      ...itemOptions.map((opt) => ({ label: `Item: ${opt.label}`, value: `item.${opt.value}` })),
      ...segmentOptions.map((opt) => ({ label: `Segment: ${opt.label}`, value: `segment.${opt.value}` })),
      ...bidItemOptions.map((opt) => ({ label: `Bid Item: ${opt.label}`, value: `bidItem.${opt.value}` })),
    ];
    return (
      <CompanySettingsCard title="Work Reports Display Fields" leftHeaderView={<WorkReportEntryHoverHelpComponent />}>
        <ABWorkReportingDisplayFieldsCard
          displayFieldOptions={options}
          displayFields={workReportEntryDisplayFields}
          title="Work Report Entry"
          propertyName="workReportEntryDisplayFields"
        />
      </CompanySettingsCard>
    );
  };

  return (
    <CompanySettingsCard title="Reporting Tab Display Fields" leftHeaderView={<HoverHelpComponent />}>
      {itemId && (
        <ABWorkReportingDisplayFieldsCard
          displayFieldOptions={itemDisplayFieldOptions}
          displayFields={itemDisplayFields}
          title="Item"
          propertyName="itemDisplayFields"
        />
      )}
      {segmentId && (
        <ABWorkReportingDisplayFieldsCard
          displayFieldOptions={segmentDisplayFieldOptions}
          displayFields={segmentDisplayFields}
          title="Segment"
          propertyName="segmentDisplayFields"
        />
      )}
      {bidItemId && (
        <ABWorkReportingDisplayFieldsCard
          displayFieldOptions={bidItemDisplayFieldOptions}
          displayFields={bidItemDisplayFields}
          title="Bid Item"
          propertyName="bidItemDisplayFields"
        />
      )}
    </CompanySettingsCard>
  );
}

ABWorkReportingDisplayFields.propTypes = {
  bucketTemplateMap: PropTypes.object.isRequired,
  abWorkReportingMapping: PropTypes.object,
  saveDisplayFields: PropTypes.func.isRequired,
  userDisplayFields: PropTypes.object,
  isWorkReportEntryMapping: PropTypes.bool,
};

ABWorkReportingDisplayFields.defaultProps = {
  abWorkReportingMapping: {},
  userDisplayFields: {},
  isWorkReportEntryMapping: false,
};
